import {
   AfterViewInit,
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   DoCheck,
   ElementRef,
   Input,
   NgZone,
   OnDestroy,
   OnInit,
   SimpleChanges,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import {
   ControlContainer,
   FormGroupDirective,
   UntypedFormArray,
   UntypedFormControl,
   UntypedFormGroup,
   Validators,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatRadioChange } from '@angular/material/radio';
import { Actions, ofType } from '@ngrx/effects';
import { ExtensionTypes, NullActionTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import {
   ExtensionChipInputOption,
   ExtensionChipInputType,
} from 'src/app/common-components/entity-select/extension/chip-input/extension-phone-number.model';
import { copyNameControlToShortNameAndDisplayName } from 'src/app/common-components/helpers/extension-form-helper';
import { phoneIntegerValidator } from 'src/app/common-components/validators/value-integer.validator';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import {
   getNullActionPlaceholder,
   getNullActionTypeNumericValue,
   setNullActionValue,
} from '../../helpers/null-action-helper';
import { ExternalNumberDetail } from '../../models/extension-detail-view.model';
import { ExtensionPrivileges } from '../../models/extension-privileges';
import { ExtensionTypeDetails } from '../../models/extension-type-details';
import { GetExtensionDetail_extension_extensionPhone } from '../../queries/generated/GetExtensionDetail';
import { changeDNDStatusSuccess } from '../../state/extension-detail.actions';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-extension-phone-details',
    styleUrls: ['./extension-phone-details.component.scss'],
    templateUrl: './extension-phone-details.component.html',
    standalone: false
})
export class ExtensionPhoneDetailsComponent
   extends ExtensionTypeDetails
   implements OnInit, OnDestroy, DoCheck, AfterViewInit
{
   @Input() extensions: [] | null;
   @Input() extensionGroups: [] | null;
   @Input() extensionPhone: GetExtensionDetail_extension_extensionPhone | null;
   @Input() accountId: number | null;
   @Input() extensionId: number | null;
   @Input() extensionPrivileges: ExtensionPrivileges;
   @Input() accountIsOtrEnabled: boolean;
   @Input() accountIsCallRecordingFeatureEnabled: boolean;
   @Input() externalNumbers: ExternalNumberDetail[];

   private _canEdit: boolean;
   @ViewChild('adminNotesArea', { read: ElementRef }) adminNotesArea: ElementRef<HTMLTextAreaElement>;
   @ViewChild('extensionNotesArea', { read: ElementRef }) extensionNotesArea: ElementRef<HTMLTextAreaElement>;
   private resizeObservers: ResizeObserver[] = [];

   @Input() set canEdit(edit: boolean) {
      if (!edit) {
         this.form?.disable();
      } else {
         this.form?.enable();
      }
      this._canEdit = edit;
   }

   form: UntypedFormGroup;

   canForwardCalls: boolean;

   isDtmfEnabled: boolean;

   forwardCallsOptionValue = 'extension';

   ringAllPhones = false;

   phoneExtensionTypes = [ExtensionTypes.Phone];

   extensionIdsToExclude: number[] = [];

   nullActions: NullActionTypes[] = [NullActionTypes.Directory, NullActionTypes.Hangup, NullActionTypes.Voicemail];

   get extensionSupervisees(): UntypedFormArray {
      const extensionSupervisees = this.form.get('extensionSupervisees') as UntypedFormArray;
      return extensionSupervisees;
   }

   searchControl = new UntypedFormControl('');

   get canEdit(): boolean {
      return this._canEdit;
   }

   private destroyed$ = new Subject<void>();

   private initDestroy$ = new Subject<void>();

   private adminNotesWasPresent = false;

   extensionGroupIdsToExclude: number[] = [];

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      private controlContainer: ControlContainer,
      public parentForm: FormGroupDirective,
      private actions$: Actions,
      private ngZone: NgZone
   ) {
      super();
      this.actions$
         .pipe(
            ofType(changeDNDStatusSuccess),
            takeUntil(this.destroyed$),
            tap((action) => {
               const dndControl = this.form.get('dndStatus');
               if (dndControl) {
                  dndControl.setValue(action.dndStatus);
                  dndControl.markAsDirty();
                  dndControl.updateValueAndValidity();
                  this.changeDetectorRef.markForCheck();
               }
            })
         )
         .subscribe();
   }

   ngOnDestroy(): void {
      this.extensionPhone = null;
      this.destroyed$.next();
      this.destroyed$.complete();

      this.initDestroy$.next();
      this.initDestroy$.complete();
      this.resizeObservers.forEach((observer) => observer.disconnect());
   }

   ngOnInit(): void {
      this.initExtensionPhoneDetails();
   }

   ngOnChanges(changes: SimpleChanges): void {
      this.extensionPhone?.forwardExtension?.extensionId || this.extensionPhone?.forwardExternalNumber?.length
         ? (this.canForwardCalls = true)
         : (this.canForwardCalls = false);

      if (this.form?.pristine) {
         this.form?.get('forwardCalls')?.setValue(this.getForwardCallsValue());
      }
      if (changes['extensionId'] && !changes['extensionId'].firstChange) {
         this.initExtensionPhoneDetails();
      }
   }

   ngDoCheck(): void {
      if (this.parentForm.form.invalid && (this.parentForm.submitted || this.form.touched)) {
         this.form.markAllAsTouched();
         this.changeDetectorRef.markForCheck();
      }
   }

   ngAfterViewInit(): void {
      // Set initial height after content is rendered
      setTimeout(() => this._setInitialTextareaHeights(), 0);

      // Then set up ResizeObserver for manual sync
      this._initNotesResizeSync();
   }
   private initExtensionPhoneDetails(): void {
      this.initDestroy$.next();
      this.initDestroy$ = new Subject<void>();

      this.form = <UntypedFormGroup>this.controlContainer.control;

      this.addForwardCallsControl();

      this.isDtmfEnabled = this.extensionPhone?.enableDtmf ?? false;
      this.setDoorDTMFFieldsValaditors();
      if (this.form.get('forwardExternalNumber')?.value) {
         this.forwardCallsOptionValue = 'external';
      } else if (this.form.get('forwardToExtension')?.value) {
         this.forwardCallsOptionValue = 'extension';
      }
      if (this.form.get('isRingAllPhones')?.value == false) {
         this.ringAllPhones = false;
         this.form.get('autoAnswerPhone')?.setValidators([Validators.required]);
      } else {
         this.ringAllPhones = true;
      }
      this.extensionIdsToExclude = [this.extensionId ?? 0];
      if (this.accountIsOtrEnabled) {
         this.form.get('isOtrEnabled')?.disable();
      }
      this.form
         .get('isRingAllPhones')
         ?.valueChanges.pipe(takeUntil(this.initDestroy$))
         .subscribe((value) => {
            this.onRingPhonesOptionChange(value);
         });
      this.form
         .get('enableDtmf')
         ?.valueChanges.pipe(takeUntil(this.initDestroy$))
         .subscribe((value) => {
            this.isDtmfEnabled = value;
            this.onEnableDTMFOptionChange();
         });

      copyNameControlToShortNameAndDisplayName(this.parentForm.form, this.form);

      const routeBusyExtension = this.parentForm.form.get('extensionPhone.routeBusyExtension');
      const routeBusyNullAction = this.parentForm.form.get('extensionPhone.routeBusyNullAction');
      const routeNoAnswerExtension = this.parentForm.form.get('extensionPhone.routeNoAnswerExtension');
      const routeNoAnswerNullAction = this.parentForm.form.get('extensionPhone.routeNoAnswerNullAction');

      let lastRouteBusyValue = { current: null };
      if (routeBusyNullAction?.value !== NullActionTypes.Unset) {
         const routeBusyValue = {
            extensionId: getNullActionTypeNumericValue(routeBusyNullAction?.value),
            name: getNullActionPlaceholder(routeBusyNullAction?.value),
            number: getNullActionTypeNumericValue(routeBusyNullAction?.value),
         };
         setNullActionValue(routeBusyValue, lastRouteBusyValue, routeBusyExtension, routeBusyNullAction, false);
      }
      routeBusyExtension?.valueChanges.pipe(takeUntil(this.initDestroy$)).subscribe((value: ExtensionOption | null) => {
         setNullActionValue(value, lastRouteBusyValue, routeBusyExtension, routeBusyNullAction);
      });
      let lastRouteNoAnswerValue = { current: null };
      if (routeNoAnswerNullAction?.value !== NullActionTypes.Unset) {
         const routeNoAnswerValue = {
            extensionId: getNullActionTypeNumericValue(routeNoAnswerNullAction?.value),
            name: getNullActionPlaceholder(routeNoAnswerNullAction?.value),
            number: getNullActionTypeNumericValue(routeNoAnswerNullAction?.value),
         };
         setNullActionValue(
            routeNoAnswerValue,
            lastRouteNoAnswerValue,
            routeNoAnswerExtension,
            routeNoAnswerNullAction,
            false
         );
      }
      routeNoAnswerExtension?.valueChanges
         .pipe(takeUntil(this.initDestroy$))
         .subscribe((value: ExtensionOption | null) => {
            setNullActionValue(value, lastRouteNoAnswerValue, routeNoAnswerExtension, routeNoAnswerNullAction);
         });
   }

   private _setInitialTextareaHeights(): void {
      const extensionNotesEl = this.extensionNotesArea?.nativeElement;
      const adminNotesEl = this.adminNotesArea?.nativeElement;
      if (!extensionNotesEl) return;

      const initialMaxHeight = 400;

      // Set both to auto first to let them grow naturally
      extensionNotesEl.style.height = 'auto';
      if (adminNotesEl) {
         adminNotesEl.style.height = 'auto';
      }

      // Calculate content-based height
      const extScrollHeight = extensionNotesEl.scrollHeight;
      const adminScrollHeight = adminNotesEl?.scrollHeight ?? 0;
      const maxContentHeight = Math.min(Math.max(extScrollHeight, adminScrollHeight), initialMaxHeight);

      extensionNotesEl.style.height = `${maxContentHeight}px`;
      if (adminNotesEl) {
         adminNotesEl.style.height = `${maxContentHeight}px`;
      }
   }

   syncTextareaHeights(): void {
      if (!this.extensionNotesArea) {
         return;
      }

      this.ngZone.runOutsideAngular(() => {
         requestAnimationFrame(() => {
            const extensionNotesTextarea = this.extensionNotesArea.nativeElement;

            extensionNotesTextarea.style.height = 'auto';
            let maxHeight = extensionNotesTextarea.scrollHeight;

            if (this.adminNotesArea) {
               const adminNotesTextarea = this.adminNotesArea.nativeElement;
               adminNotesTextarea.style.height = 'auto';
               const adminNotesHeight = adminNotesTextarea.scrollHeight;
               maxHeight = Math.max(maxHeight, adminNotesHeight);

               adminNotesTextarea.style.height = `${maxHeight}px`;
            }

            extensionNotesTextarea.style.height = `${maxHeight}px`;
         });
      });
   }

   ngAfterViewChecked(): void {
      const adminElExists = !!this.adminNotesArea?.nativeElement;

      if (adminElExists && !this.adminNotesWasPresent) {
         this.adminNotesWasPresent = true;

         this._setInitialTextareaHeights();
         this._initNotesResizeSync();
      } else if (!adminElExists) {
         this.adminNotesWasPresent = false;
      }
   }

   private _initNotesResizeSync(): void {
      this.resizeObservers.forEach((observer) => observer.disconnect());
      this.resizeObservers = [];

      const adminNotesEl = this.adminNotesArea?.nativeElement;
      const extensionNotesEl = this.extensionNotesArea?.nativeElement;
      if (!extensionNotesEl) return;

      const observe = (source: HTMLTextAreaElement, target: HTMLTextAreaElement | undefined) => {
         if (!target) return;

         const observer = new ResizeObserver(() => this._syncManualResize(source, target));
         observer.observe(source);
         this.resizeObservers.push(observer);
      };

      observe(extensionNotesEl, adminNotesEl);
      if (adminNotesEl) {
         observe(adminNotesEl, extensionNotesEl);
      }
   }

   private _syncManualResize(source: HTMLTextAreaElement, target: HTMLTextAreaElement): void {
      this.ngZone.runOutsideAngular(() => {
         const sourceHeight = source.offsetHeight;
         const targetHeight = target.offsetHeight;

         if (targetHeight !== sourceHeight) {
            target.style.height = `${sourceHeight}px`;
         }
      });
   }

   onForwardOptionChange(event: MatRadioChange) {
      this.forwardCallsOptionValue = event.value;
      if (this.forwardCallsOptionValue === 'extension') {
         this.form.get('forwardExternalNumber')?.setValue(null);
         this.form.get('forwardExternalNumber')?.markAsDirty();
         this.form.get('forwardExternalNumber')?.clearValidators();
         this.form.get('forwardToExtension')?.markAsTouched();
         this.form.get('forwardToExtension')?.setValidators([Validators.required]);
         this.form.get('forwardToExtension')?.updateValueAndValidity();
         this.form.get('forwardExternalNumber')?.updateValueAndValidity();
      } else if (this.forwardCallsOptionValue === 'external') {
         this.form.get('forwardToExtension')?.setValue(null);
         this.form.get('forwardToExtension')?.markAsDirty();
         this.form.get('forwardToExtension')?.clearValidators();
         this.form.get('forwardToExtension')?.updateValueAndValidity();
         this.form
            .get('forwardExternalNumber')
            ?.setValidators([
               Validators.required,
               Validators.minLength(10),
               Validators.maxLength(10),
               Validators.pattern(/^\d+$/),
            ]);
         this.form.get('forwardExternalNumber')?.markAsTouched();
         this.form.get('forwardExternalNumber')?.updateValueAndValidity();
      }
   }

   onCanForwardCallsChange(checkbox: MatCheckboxChange) {
      this.canForwardCalls = checkbox.checked;
      if (!this.canForwardCalls) {
         this.form.get('forwardExternalNumber')?.setValue(null);
         this.form.get('forwardToExtension')?.setValue(null);
         this.form.get('forwardCalls')?.setValue(null);
         this.form.get('forwardCalls')?.clearValidators();
         this.form.get('forwardCalls')?.updateValueAndValidity();
      } else {
         this.form.get('forwardCalls')?.markAsTouched();
         this.form.get('forwardCalls')?.setValidators(Validators.required);
         this.form.get('forwardCalls')?.updateValueAndValidity();
      }
      this.form.get('forwardExternalNumber')?.markAsDirty();
      this.form.get('forwardToExtension')?.markAsDirty();
   }

   onEnableDTMFOptionChange() {
      this.form.get('secondsToWait')?.setValue(null);
      this.form.get('dtmfToPlay')?.setValue(null);
      this.setDoorDTMFFieldsValaditors();
   }

   setDoorDTMFFieldsValaditors() {
      if (this.isDtmfEnabled) {
         this.form
            .get('secondsToWait')
            ?.setValidators([Validators.required, Validators.min(0), Validators.max(20), phoneIntegerValidator()]);
         this.form
            .get('dtmfToPlay')
            ?.setValidators([Validators.required, Validators.maxLength(10), Validators.pattern(/^[0-9*#]+$/)]);
      } else {
         this.form.get('secondsToWait')?.setValidators(null);
         this.form.get('dtmfToPlay')?.setValidators(null);
      }
      this.form.get('secondsToWait')?.updateValueAndValidity();
      this.form.get('dtmfToPlay')?.updateValueAndValidity();
   }

   isSuperviseesEnabled(): boolean {
      return this.extensionPrivileges.isManageSuperviseesEnabled ?? false;
   }

   onRingPhonesOptionChange(value: boolean) {
      this.ringAllPhones = value;
      if (this.ringAllPhones === true) {
         this.form.get('autoAnswerPhone')?.setValue(null);
         this.form.get('autoAnswerPhone')?.markAsDirty();
         this.form.get('autoAnswerPhone')?.setValidators(null);
         this.form.get('autoAnswerPhone')?.updateValueAndValidity();
         this.form.get('isAutoAnswer')?.setValue(false);
         this.form.get('isAutoAnswer')?.markAsDirty();
         this.form.get('isAutoAnswer')?.setValidators(null);
         this.form.get('isAutoAnswer')?.updateValueAndValidity();
      } else {
         this.form.get('autoAnswerPhone')?.markAsTouched();
         this.form.get('autoAnswerPhone')?.setValidators([Validators.required]);
         this.form.get('autoAnswerPhone')?.updateValueAndValidity();
         this.form.get('isAutoAnswer')?.markAsTouched();
         this.form.get('isAutoAnswer')?.updateValueAndValidity();
      }
      this.changeDetectorRef.markForCheck();
   }

   private addForwardCallsControl() {
      let forwardCallsValue: ExtensionChipInputOption[] | null = this.getForwardCallsValue();

      const forwardCallsControl = new UntypedFormControl(forwardCallsValue);
      this.form.addControl('forwardCalls', forwardCallsControl);

      forwardCallsControl?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe((chips: ExtensionChipInputOption[]) => {
            if (chips) {
               const chipValue = chips.find((chip) => !chip.isDeleted);
               if (chipValue) {
                  if (chipValue.type === 1) {
                     const extension: ExtensionOption = chipValue.value as ExtensionOption;
                     this.form.get('forwardToExtension')?.patchValue(extension);
                  } else {
                     let phoneNumber: string | null = null;

                     if (typeof chipValue.value === 'string') {
                        phoneNumber = chipValue.value;
                     } else if (
                        chipValue.value &&
                        typeof chipValue.value === 'object' &&
                        'externalNumber' in chipValue.value
                     ) {
                        phoneNumber = (chipValue.value as ExternalNumberDetail).externalNumber.toString();
                     }

                     this.form.get('forwardExternalNumber')?.patchValue(phoneNumber);
                  }
               } else {
                  this.form.get('forwardExternalNumber')?.patchValue(null);
                  this.form.get('forwardToExtension')?.patchValue(null);
                  this.form.get('forwardExternalNumber')?.markAsDirty();
                  this.form.get('forwardToExtension')?.markAsDirty();
               }
            }
         });
   }

   private getForwardCallsValue(): ExtensionChipInputOption[] | null {
      let forwardCallsValue: ExtensionChipInputOption[] | null = null;
      let forwardExtensionValue: ExtensionOption | null = this.form.get('forwardToExtension')?.value;
      let forwardExternalNumberValue: string | null = this.form.get('forwardExternalNumber')?.value;

      if (forwardExtensionValue && forwardExtensionValue.extensionId) {
         forwardCallsValue = [
            {
               stepId: 0,
               type: ExtensionChipInputType.Extension,
               value: forwardExtensionValue,
               isDeleted: false,
            },
         ];
      } else if (forwardExternalNumberValue) {
         forwardCallsValue = [
            {
               stepId: 0,
               type: ExtensionChipInputType.PhoneNumber,
               value: forwardExternalNumberValue,
               isDeleted: false,
            },
         ];
      }

      return forwardCallsValue;
   }
}
